import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { formatMoney } from "@/lib/currency";

const STAGES = [
  "Sourcing & Acquisition", "Cleaning & Repairing", "Ironing", "Weighing",
  "Photographing", "Studio Editing", "Listing & Marketing", "Sale Completion",
  "Packaging", "Shipping", "Confirmation of Receipt",
];

export default function AdminPipeline() {
  const [items, setItems] = useState([]);
  const [dragged, setDragged] = useState(null);
  const scrollRef = useRef(null);
  const [scrollState, setScrollState] = useState({ canLeft: false, canRight: true });

  const load = useCallback(() => api.get("/inventory").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  // Track whether the kanban can be scrolled left/right so we can show/hide chevron buttons.
  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setScrollState({
      canLeft: el.scrollLeft > 5,
      canRight: el.scrollLeft + el.clientWidth < el.scrollWidth - 5,
    });
  }, []);
  useEffect(() => {
    updateScrollState();
    window.addEventListener("resize", updateScrollState);
    return () => window.removeEventListener("resize", updateScrollState);
  }, [updateScrollState, items.length]);

  const scrollByColumn = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    // 288px column + 20px gap ≈ 308px per step
    el.scrollBy({ left: dir * 308, behavior: "smooth" });
    // Update state after the scroll animation ~500ms
    setTimeout(updateScrollState, 550);
  };

  const onDrop = async (stage) => {
    if (!dragged) return;
    if (dragged.stage === stage) return;
    try {
      await api.patch(`/inventory/${dragged.id}/stage`, { stage });
      toast.success(`moved to ${stage.toLowerCase()}.`);
      load();
    } catch {
      toast.error("could not move piece.");
    } finally {
      setDragged(null);
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-pipeline">
        <span className="editorial-label">workflow · pipeline</span>
        <h1 className="font-serif text-5xl lowercase text-charcoal mt-3 mb-2">from sourcing to delivered.</h1>
        <p className="text-espresso max-w-2xl mb-10">every piece passes through eleven careful steps. drag a card to advance its story.</p>

        <div className="relative -mx-4 sm:-mx-6 lg:-mx-10">
          {/* Left chevron scroll button */}
          {scrollState.canLeft && (
            <button
              onClick={() => scrollByColumn(-1)}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-charcoal text-ivory hover:bg-brass w-10 h-10 flex items-center justify-center shadow-lg"
              data-testid="pipeline-scroll-left"
              aria-label="scroll left"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
          {/* Right chevron scroll button */}
          {scrollState.canRight && (
            <button
              onClick={() => scrollByColumn(1)}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-charcoal text-ivory hover:bg-brass w-10 h-10 flex items-center justify-center shadow-lg"
              data-testid="pipeline-scroll-right"
              aria-label="scroll right"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
          {/* Right-edge fade hint indicating more content off-screen */}
          {scrollState.canRight && (
            <div className="absolute right-0 top-0 bottom-6 w-16 bg-gradient-to-l from-ivory to-transparent pointer-events-none z-[5]" />
          )}
          {scrollState.canLeft && (
            <div className="absolute left-0 top-0 bottom-6 w-16 bg-gradient-to-r from-ivory to-transparent pointer-events-none z-[5]" />
          )}

          <div
            ref={scrollRef}
            onScroll={updateScrollState}
            className="kanban-scroll kanban-scroll-visible overflow-x-auto pb-6 px-4 sm:px-6 lg:px-10"
            data-testid="pipeline-scroll-container"
          >
            <div className="flex gap-5 min-w-max">
              {STAGES.map((stage, idx) => {
                const cards = items.filter((i) => i.stage === stage);
                return (
                  <div
                    key={stage}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => onDrop(stage)}
                    className="w-72 bg-linen border border-taupe/50 flex-shrink-0 flex flex-col"
                    data-testid={`column-${idx}`}
                  >
                    <div className="px-4 py-4 border-b border-taupe/50 flex items-center justify-between">
                      <div>
                        <p className="text-xs text-brass tracking-widest">step {String(idx + 1).padStart(2, "0")}</p>
                        <h3 className="font-serif text-lg lowercase text-charcoal">{stage.toLowerCase()}</h3>
                      </div>
                      <span className="editorial-label">{cards.length}</span>
                    </div>
                    <div className="p-3 space-y-3 min-h-[400px]">
                      {cards.map((c, ci) => (
                        <Link
                          key={c.id}
                          to={`/admin/inventory/${c.id}`}
                          draggable
                          onDragStart={() => setDragged(c)}
                          className="block bg-ivory border border-taupe/40 p-3 hover:border-brass transition-colors cursor-grab active:cursor-grabbing"
                          data-testid={`card-${idx}-${ci}`}
                        >
                          <div className="aspect-[4/3] bg-linen overflow-hidden mb-2">
                            {c.photos?.[0] ? <img src={fileUrl(c.photos[0])} alt="" className="w-full h-full object-cover" /> : null}
                          </div>
                          <p className="font-serif text-base lowercase text-charcoal">{c.title}</p>
                          <p className="text-xs text-taupe mt-1">{c.brand || "—"} · {formatMoney(c.sale_price)}</p>
                        </Link>
                      ))}
                      {cards.length === 0 && (
                        <p className="text-center text-xs text-taupe py-6 italic">awaiting pieces</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
